import type { DoneInvokeEvent, EventObject, PropertyMapper } from 'xstate';
import { createMachine, assign } from 'xstate';
import type { SubscriptionInfo } from '~/subscription-management/api';
import { subscriptionApi } from '~/subscription-management/api';
import { getSubscriptionInfo } from '~/subscription-management/api/subscription/get-subscription-info';
import { FORM_NAME, trackFormSubmit } from '~/subscription-management/utils/tracking';
import type { UpdatePaymentDetailsEvent } from '../checkout.machine.types';
import type { PaymentDetailsContext, PaymentDetailsEvent } from './payment-details.machine.types';

export const paymentDetailsMachine = createMachine(
  {
    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    tsTypes: {} as import('./payment-details.machine.typegen').Typegen0,
    id: 'payment-details',
    schema: {
      context: {} as PaymentDetailsContext,
      events: {} as PaymentDetailsEvent,
      services: {} as {
        updatePaymentMethodService: { data: SubscriptionInfo };
      },
    },
    initial: 'edit',
    states: {
      edit: {},
      update: {
        invoke: {
          id: 'updatePaymentMethodService',
          src: 'updatePaymentMethodService',
          onDone: [
            {
              actions: assign<PaymentDetailsContext, DoneInvokeEvent<SubscriptionInfo>>({
                customer: (context, { data }) => ({
                  ...context.customer,
                  paymentMethod: data.customer?.paymentMethod,
                }),
              }),
              target: 'done',
            },
          ],
          onError: [
            {
              target: 'error',
              actions: [
                (context) => {
                  trackFormSubmit({
                    name: FORM_NAME.updatePayment,
                    submitSuccess: false,
                    accountId: context.accountId,
                    errorMessage: 'Failed to update payment method',
                  });
                },
              ],
            },
          ],
        },
      },
      error: {},
      done: {
        type: 'final',
        data: {
          customer: (context) => context.customer,
        } as PropertyMapper<PaymentDetailsContext, EventObject, PaymentDetailsContext>,
      },
    },
    on: {
      UPDATE_PAYMENT_DETAILS: [
        {
          cond: 'isAmendmentOrReactivation',
          actions: 'assignPaymentMethodId',
          target: 'update',
        },
        { actions: 'assignPaymentMethodId', target: 'done' },
      ],
    },
  },
  {
    services: {
      updatePaymentMethodService: (context, event) => {
        return subscriptionApi
          .updatePaymentMethod({
            paymentMethodId: event.refId,
            hotjarAccountId: context.accountId,
          })
          .then(() => getSubscriptionInfo());
      },
    },
    guards: {
      isAmendmentOrReactivation: (context: PaymentDetailsContext) =>
        context.isAmendmentOrReactivation,
    },
    actions: {
      assignPaymentMethodId: assign<PaymentDetailsContext, UpdatePaymentDetailsEvent>({
        customer: (context, event) => ({
          ...context.customer,
          paymentMethodExternalId: event.refId,
        }),
      }),
    },
  },
);
